import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Download, AlertTriangle, Boxes } from "lucide-react";
import { RuleLegend, DEFAULT_RULES, ruleShort, ruleError } from "@/components/minmax/RuleLegend";

const CAT_STYLE = {
  AA: "bg-rose-100 text-rose-800 border-rose-200",
  A: "bg-orange-100 text-orange-800 border-orange-200",
  BB: "bg-amber-100 text-amber-800 border-amber-200",
  B: "bg-lime-100 text-lime-800 border-lime-200",
  CC: "bg-sky-100 text-sky-800 border-sky-200",
  C: "bg-slate-100 text-slate-700 border-slate-200",
  D: "bg-slate-100 text-slate-500 border-slate-200",
};
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));
const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function MinMaxPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [cat, setCat] = useState("all");
  const [lab, setLab] = useState("all");
  const [q, setQ] = useState("");
  const [onlyOver, setOnlyOver] = useState(false);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [recalculating, setRecalculating] = useState(false);

  const custom = JSON.stringify(rules) !== JSON.stringify(DEFAULT_RULES);
  const rulesValid = Object.values(rules).every((r) => !ruleError(r));
  const rulesParam = custom ? JSON.stringify(rules) : "";

  useEffect(() => {
    if (!rulesValid) return undefined;
    const first = data == null;
    const timer = setTimeout(() => {
      if (first) setLoading(true); else setRecalculating(true);
      api.get("/minmax", { params: { window_months: 12, rules: rulesParam || undefined } })
        .then((r) => setData(r.data))
        .catch((e) => toast.error(e?.response?.data?.detail || "Error al calcular mínimos y máximos"))
        .finally(() => { setLoading(false); setRecalculating(false); });
    }, first ? 0 : 400);
    return () => clearTimeout(timer);
  }, [rulesParam, rulesValid]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeRule = (code, field, value) =>
    setRules((prev) => ({ ...prev, [code]: { ...prev[code], [field]: value } }));

  const labs = useMemo(() => {
    const s = new Set((data?.items || []).map((i) => i.laboratorio).filter(Boolean));
    return [...s].sort();
  }, [data]);

  const rows = useMemo(() => {
    let list = data?.items || [];
    if (type !== "all") list = list.filter((i) => i.type === type);
    if (cat !== "all") list = list.filter((i) => i.category === cat);
    if (lab !== "all") list = list.filter((i) => i.laboratorio === lab);
    if (onlyOver) list = list.filter((i) => i.over_max > 0);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((i) =>
        i.cn.includes(s) || (i.name || "").toLowerCase().includes(s) || (i.gh || "").toLowerCase().includes(s));
    }
    return list;
  }, [data, type, cat, lab, q, onlyOver]);

  const download = async (mode = "filtered") => {
    const t = toast.loading("Generando Excel…");
    try {
      const params = mode === "filtered"
        ? { window_months: 12, type, category: cat, laboratorio: lab, only_over: onlyOver, q, group_by: "category" }
        : { window_months: 12, group_by: "type" };
      if (rulesParam) params.rules = rulesParam;
      const res = await api.get("/minmax/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = mode === "filtered" ? "minimos_maximos_filtrado.xlsx" : "minimos_maximos.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar", { id: t });
    }
  };

  if (loading) {
    return <div className="p-8 lg:p-12 text-slate-500" data-testid="minmax-loading">Calculando rotación…</div>;
  }
  if (!data || data.totals.cns === 0) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl" data-testid="minmax-empty">
        <h1 className="font-display text-3xl font-bold text-slate-900">Mínimos y máximos propuestos</h1>
        <p className="text-slate-600 mt-3">
          Todavía no hay dispensaciones en los últimos 12 meses para calcular la rotación. Sube un estudio y vuelve aquí.
        </p>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="p-8 lg:p-12 max-w-[1500px]" data-testid="minmax-page">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Reposición</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Mínimos y máximos propuestos</h1>
      <p className="text-slate-600 mt-2 max-w-3xl text-sm">
        Clasificación de los {fmtNum(t.cns)} códigos dispensados en los últimos 12 meses
        ({data.months_covered} mes(es) con datos) más los que tienes en stock sin ventas (categoría D).
        VD = venta diaria = uds/año ÷ 365. En AA y A se aplica un suelo de 1 unidad.
      </p>

      <p className="text-xs text-slate-500 mt-2 max-w-3xl bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="minmax-no-description-note">
        <strong className="text-slate-700">Las líneas sin descripción</strong> son códigos que no son ni genérico ni marca
        (no tienen genérico equivalente), por eso no traen nombre ni grupo homogéneo. Aun así, <strong className="text-slate-700">el CN
        te sirve igual</strong> para revisar su stock, ajustar el mínimo/máximo y mejorar la reposición.
      </p>

      <RuleLegend rules={rules} catStyle={CAT_STYLE} onChange={changeRule} onReset={() => setRules(DEFAULT_RULES)}
        custom={custom} recalculating={recalculating} />

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <button
          onClick={() => download("filtered")}
          data-testid="minmax-export-filtered"
          title="Exporta exactamente el listado que estás viendo, con una hoja por categoría de rotación"
          className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
        >
          <Download className="h-4 w-4" />
          Exportar Excel ({fmtNum(rows.length)} líneas con los filtros actuales)
        </button>
        <button
          onClick={() => download("all")}
          data-testid="minmax-export"
          title="Exporta todos los códigos, con una hoja para marca y otra para genérico"
          className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
        >
          <Download className="h-4 w-4" />
          Exportar todo (marca / genérico)
        </button>
        {!data.has_stock && (
          <span className="inline-flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <Boxes className="h-3.5 w-3.5" />
            Sube tu fichero de stock para ver excesos y faltas
          </span>
        )}
        {data.has_stock && t.over_max > 0 && (
          <span className="inline-flex items-center gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="minmax-over-alert">
            <AlertTriangle className="h-3.5 w-3.5" />
            {fmtNum(t.over_max)} CN por encima del máximo · {fmtNum(t.over_max_units)} uds · {fmtEur(t.over_max_euros)} inmovilizados
          </span>
        )}
        {data.low_history && (
          <span className="inline-flex items-center gap-2 text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="minmax-low-history">
            <AlertTriangle className="h-3.5 w-3.5" />
            Solo {data.months_covered} mes(es) de historial: con tan poco periodo la categoría C (1-3 uds/año) no puede aparecer — sube más meses de dispensación
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-6" data-testid="minmax-summary">
        {Object.entries(data.summary).map(([code, s]) => (
          <button
            key={code}
            onClick={() => setCat(cat === code ? "all" : code)}
            data-testid={`minmax-cat-${code}`}
            className={`text-left border rounded-lg p-3 transition-colors ${
              cat === code ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"
            }`}
          >
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${CAT_STYLE[code]}`}>
              {code}
            </span>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            <div className="font-mono-num text-xl font-bold text-slate-900">{fmtNum(s.count)}</div>
            <div className="text-[10px] text-slate-500 font-mono-num">{ruleShort(code, data.rules?.[code] || DEFAULT_RULES[code])}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6">
        <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-xs font-semibold">
          {[["all", "Todos"], ["marca", "Marca"], ["generico", "Genérico"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setType(v)}
              data-testid={`minmax-type-${v}`}
              className={`px-3 py-1.5 rounded ${type === v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
            >
              {l} {v === "marca" ? `(${fmtNum(t.marca)})` : v === "generico" ? `(${fmtNum(t.generico)})` : ""}
            </button>
          ))}
        </div>
        <select
          value={lab}
          onChange={(e) => setLab(e.target.value)}
          data-testid="minmax-lab-filter"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white max-w-[240px]"
        >
          <option value="all">Todos los laboratorios</option>
          {labs.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar CN, producto o G.H…"
          data-testid="minmax-search"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1 min-w-[220px]"
        />
        <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
          <input
            type="checkbox"
            checked={onlyOver}
            onChange={(e) => setOnlyOver(e.target.checked)}
            data-testid="minmax-only-over"
          />
          Sólo con exceso de stock
        </label>
      </div>

      <div className="mt-4 border border-slate-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm" data-testid="minmax-table">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">CN</th>
              <th className="text-left px-3 py-2">Producto</th>
              <th className="text-left px-3 py-2">Laboratorio</th>
              <th className="px-3 py-2">Cat</th>
              <th className="text-right px-3 py-2">Uds/año</th>
              <th className="text-right px-3 py-2">V. diaria</th>
              <th className="text-right px-3 py-2">Mín</th>
              <th className="text-right px-3 py-2">Máx</th>
              <th className="text-right px-3 py-2">Stock</th>
              <th className="text-right px-3 py-2">Exceso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.slice(0, 400).map((i) => (
              <tr key={i.cn} className="hover:bg-slate-50" data-testid={`minmax-row-${i.cn}`}>
                <td className="px-3 py-2 font-mono-num">{i.cn}</td>
                <td className="px-3 py-2 max-w-[300px]">
                  <span
                    className={`block truncate ${i.name ? "" : "italic text-slate-400"}`}
                    title={i.name || "Sin descripción: este CN no es ni genérico ni marca (no tiene genérico). Puedes usar el CN para trabajarlo igualmente."}
                  >
                    {i.name || "Sin descripción (usa el CN)"}
                  </span>
                  <span className="block truncate text-[10px] text-slate-500" title={i.gh}>{i.gh || ""}</span>
                </td>
                <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate" title={i.laboratorio}>{i.laboratorio}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${CAT_STYLE[i.category]}`}>
                    {i.category}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtNum(i.units_year)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-slate-500">{i.daily_sales}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold">{i.min_prop}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold">{i.max_prop}</td>
                <td className="px-3 py-2 text-right font-mono-num">{i.stock == null ? "—" : fmtNum(i.stock)}</td>
                <td className={`px-3 py-2 text-right font-mono-num font-bold ${i.over_max > 0 ? "text-rose-700" : "text-slate-300"}`}>
                  {i.over_max > 0 ? `+${i.over_max}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 400 && (
          <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200">
            Mostrando 400 de {fmtNum(rows.length)} · usa los filtros o descarga el Excel completo
          </div>
        )}
        {rows.length === 0 && (
          <div className="px-3 py-8 text-center text-sm text-slate-500">Ningún CN con esos filtros</div>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Criterio: AA &gt;365 uds/año · A 52-365 · BB 35-51 · B 12-34 · CC 4-11 · C 1-3 · D sin ventas
      </div>
    </div>
  );
}
